import { TargetingConfig } from "./Bundles/Types";

export const validateTargeting = (targeting: TargetingConfig) => {
    if (!targeting) return "Targeting configuration is required";

    // Validate triggerType
    if (targeting.triggerType) {
        if (!["ALL_PRODUCTS", "SPECIFIC_PRODUCTS", "EXCLUDE_PRODUCTS"].includes(targeting.triggerType)) {
            return "Invalid trigger type";
        }

        // Validate triggerProducts if specific/exclude products are selected
        if (
            (targeting.triggerType === "SPECIFIC_PRODUCTS" || targeting.triggerType === "EXCLUDE_PRODUCTS") &&
            (!targeting.triggerProducts || targeting.triggerProducts.length === 0)
        ) {
            return "Product selection is required for specific/exclude products trigger";
        }
    }

    // Validate cart trigger
    if (targeting.hasCartTrigger) {
        if (!targeting.cartTrigger) {
            return "Cart trigger configuration is required when cart trigger is enabled";
        }

        // Validate cart trigger type
        if (!["CART_TOTAL", "CART_ITEMS"].includes(targeting.cartTrigger.type)) {
            return "Invalid cart trigger type";
        }

        // Validate cart trigger operator
        if (!["GREATER_THAN", "LESS_THAN", "EQUAL_TO"].includes(targeting.cartTrigger.operator)) {
            return "Invalid cart trigger operator";
        }

        // Validate cart trigger value
        if (typeof targeting.cartTrigger.value !== "number" || targeting.cartTrigger.value < 0) {
            return "Cart trigger value must be a non-negative number";
        }
    }

    return "";
};

export const getUpdatedTargeting = (targeting: TargetingConfig): TargetingConfig => {
    const updateTargeting: TargetingConfig = { ...targeting };
    const isAllTargeting =
        targeting.includeCustomers === "all" &&
        targeting.excludeCustomers === "none" &&
        (targeting.includeLocations?.length ?? 0) === 0 &&
        (targeting.excludeLocations?.length ?? 0) === 0;

    if (targeting?.hasOwnProperty("includeCustomers") && targeting?.hasOwnProperty("excludeCustomers")) {
        updateTargeting.targetingType = isAllTargeting ? "all" : "specific";
        // tags
        updateTargeting.customerTagsEnabled =
            targeting?.includeCustomers == "tag" || targeting?.excludeCustomers == "tag";
        updateTargeting.customerTagType = targeting?.excludeCustomers == "tag" ? "exclude" : "include";
        updateTargeting.customerTags =
            targeting?.includeCustomers == "tag" || targeting?.excludeCustomers == "tag"
                ? targeting?.includeCustomers == "tag"
                    ? targeting?.includeCustomerData
                    : targeting?.excludeCustomerData
                : "";
        // spent
        updateTargeting.customerSpentEnabled =
            targeting?.includeCustomers == "lifetimeSpent" || targeting?.excludeCustomers == "lifetimeSpent";
        updateTargeting.customerSpentType = "greater_than";
        updateTargeting.customerSpent =
            targeting?.includeCustomers == "lifetimeSpent" || targeting?.excludeCustomers == "lifetimeSpent"
                ? targeting?.includeCustomers == "lifetimeSpent"
                    ? targeting?.includeCustomerData
                    : targeting?.excludeCustomerData
                : "";
    }
    // country
    if (targeting?.hasOwnProperty("includeLocations") && targeting?.hasOwnProperty("excludeLocations")) {
        updateTargeting.customerCountriesEnabled = !!(
            targeting.includeLocations.length || targeting.excludeLocations.length
        );
        updateTargeting.customerCountryType = targeting.includeLocations.length
            ? "include"
            : targeting.excludeLocations?.length
            ? "exclude"
            : "include";
        updateTargeting.customerCountries = targeting.includeLocations.length
            ? targeting.includeLocations
            : targeting.excludeLocations;
    }

    return updateTargeting;
};

const shouldRenderTargeting = (config: TargetingConfig): boolean => {
    if (!config) return true;

    const _targeting: TargetingConfig = { ...config, ...getUpdatedTargeting(config) };

    const checkTargetingType = () => {
        if (_targeting?.targetingType == "specific") {
            let isTagsChecked = true;
            let isCountriesChecked = true;
            let isSpentChecked = true;
            if (_targeting?.customerTagsEnabled) {
                const bundleTags = window.kefi_bundles.customer_tags?.split(",") || [];
                const targetTags = _targeting.customerTags?.split(",") || [];
                if (_targeting.customerTagType === "include") {
                    isTagsChecked = bundleTags.some((tag) => targetTags.includes(tag));
                } else if (_targeting.customerTagType === "exclude") {
                    isTagsChecked = !bundleTags.some((tag) => targetTags.includes(tag));
                } else {
                    isTagsChecked = false;
                }
            }
            if (_targeting?.customerSpentEnabled && typeof window.kefi_bundles.customer_spent === "number") {
                const lifetimeSpent = window.kefi_bundles.customer_spent / 100;
                const targetSpent = +_targeting.customerSpent * window.kefi_bundles.localization.rate;
                if (_targeting?.customerSpentType == "greater_than" && lifetimeSpent >= targetSpent) {
                    isSpentChecked = true;
                } else if (_targeting?.customerSpentType == "less_than" && lifetimeSpent <= targetSpent) {
                    isSpentChecked = true;
                } else {
                    isSpentChecked = false;
                }
            }
            if (_targeting?.customerCountriesEnabled && _targeting.customerCountries?.length) {
                const visitorCountries: any = window.kefi_bundles.localization.country?.split(",") ?? [];
                const targetCountries = _targeting.customerCountries ?? [];
                if (visitorCountries.length && _targeting.customerCountryType === "include") {
                    isCountriesChecked = !!visitorCountries.some((country: string) =>
                        targetCountries.includes(country),
                    );
                } else if (visitorCountries.length && _targeting.customerCountryType === "exclude") {
                    isCountriesChecked = !visitorCountries.some((country: string) => targetCountries.includes(country));
                } else {
                    isCountriesChecked = false;
                }
            }

            return isTagsChecked && isCountriesChecked && isSpentChecked;
        } else {
            console._log("===Targeting Targeting", {
                Type: _targeting?.targetingType + _targeting.customerCountryType,
                cn: _targeting.customerCountries,
            });
            return true;
        }
    };

    const shouldRenderComponent = () => {
        try {
            return checkTargetingType();
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    return shouldRenderComponent();
};

export default shouldRenderTargeting;
